import React from 'react'
import { useSelector } from 'react-redux'
import AbstractCanvas from './AbstractCanvas'
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '../utils/settings'
import { getImage } from '../utils/image-loader'
import { BikeAngle, State } from 'shared'

interface Placement {
  x: number
  y: number
  width: number
  height: number
  rotation?: number
}

interface Logo {
  imgUrl: string
  placement: Placement
  angle?: BikeAngle
}

interface BikeImageLogoCanvasProps {
  logo: Logo
}

const BikeImageLogoCanvas: React.FC<BikeImageLogoCanvasProps> = ({ logo }) => {
  const currentAngle = useSelector((state: State) => state.bikeConfig.angle)
  const zIndex = 1000
  const name = 'logo'
  const { imgUrl, placement, angle = 'none' } = logo
  const paintData = { imgUrl, placement }

  const shouldRepaint = (a: typeof paintData, b: typeof paintData) => {
    return a.imgUrl !== b.imgUrl || a.placement !== b.placement
  }

  const paint = (
    ctx: CanvasRenderingContext2D,
    {
      imgUrl,
      placement = { x: 0, y: 0, width: 0, height: 0 }
    }: typeof paintData
  ) => {
    const img = getImage(imgUrl).getImageObj()

    const { x, y, width, height } = fitLogoPlacement(img, placement)

    // Reset
    ctx.restore()
    ctx.save()
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

    // Rotate around the center of the logo
    ctx.translate(placement.x, placement.y)
    ctx.rotate(((placement.rotation || 0) * Math.PI) / 180)
    ctx.translate(x, y)

    if (img) {
      // Draw logo
      ctx.drawImage(img, 0, 0, width, height)
    }
  }

  if (angle !== currentAngle) return null
  return (
    <AbstractCanvas
      className=""
      zIndex={zIndex}
      name={name}
      paintData={paintData}
      shouldRepaint={shouldRepaint}
      paint={paint}
    />
  )
}

function fitLogoPlacement(
  img: HTMLImageElement | undefined,
  placement: Placement
) {
  if (img) {
    let aspectRatio = img.width / img.height
    let sizeRatio = placement.width / placement.height

    let renderWidth = placement.width
    let renderHeight = placement.height
    let x = 0
    let y = 0

    if (aspectRatio < sizeRatio) {
      renderWidth = img.width * (renderHeight / img.height)
      x = (placement.width - renderWidth) / 2
    } else if (sizeRatio < aspectRatio) {
      renderHeight = img.height * (renderWidth / img.width)
      y = (placement.height - renderHeight) / 2
    }

    return {
      width: renderWidth,
      height: renderHeight,
      x,
      y
    }
  }
  return {
    width: 0,
    height: 0,
    x: 0,
    y: 0
  }
}

export default BikeImageLogoCanvas
